import React, { useState, useRef, useEffect } from 'react';
import { 
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify, 
  Table, Image as ImageIcon, Mic, Sparkles, Undo, Redo, Eraser, 
  Calendar, Loader2, Save, Trash2, Plus, FileText, CheckCircle2, AlertCircle, Play, BrainCircuit, X
} from 'lucide-react';
import { Patient, Approach, ClinicalRecord, AudioAnalysis } from '../types';
import { analyzeSessionText, registerAiConsent, superviseSession, transcribeAudio, type SupervisionApiResponse } from '../services/aiApi';
import { createClinicalRecord, deleteClinicalRecord, updateClinicalRecord } from '../services/patientsApi';
import { getRecordingPlaybackUrl } from '../services/recordingsApi';
import { describeError } from '../utils/describeError';
import Recordings from './Recordings';
import AIAnalysis from './AIAnalysis';
import Transcriptions from './Transcriptions';
import { buildTableHtml, clampCols, clampRows, TABLE_MAX_COLS, TABLE_MAX_ROWS } from '../utils/richText';

interface ProntuarioProps {
  patient: Patient;
  onUpdatePatient: (patient: Patient) => void;
  patients: Patient[];
  recordingsList: any[];
  setRecordingsList: React.Dispatch<React.SetStateAction<any[]>>;
  onSaveAIAnalysis: (patientId: string, recordingId: string, analysis: AudioAnalysis, attachToProntuario?: boolean) => void;
  onDeleteTranscription: (recordingId: string) => void;
  initialRecordDate?: string;
  onClearInitialRecordDate?: () => void;
  /**
   * Avisa o App que o prontuário do paciente mudou (após salvar/excluir na
   * API), para o estado global refletir sem recarregar tudo.
   */
  onRecordsChange?: (patientId: string, records: ClinicalRecord[]) => void;
}

/** Escapa texto vindo da IA antes de inserir como HTML no editor. */
const escapeHtml = (text: string) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const textToHtml = (text: string) => escapeHtml(text).replace(/\n/g, '<br/>');

/** Lado máximo da imagem embutida no prontuário (o registro vai para a API). */
const MAX_IMAGE_SIDE = 1400;

/**
 * Reduz a imagem antes de embutir: uma foto de celular em base64 passa de
 * 5 MB e estourava o limite da API — o prontuário "não salvava".
 */
const readImageAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    reader.onload = () => {
      if (typeof reader.result !== 'string') return reject(new Error('Imagem inválida.'));
      const original = reader.result;
      const img = new Image();
      img.onload = () => {
        const maior = Math.max(img.width, img.height);
        if (!maior || (maior <= MAX_IMAGE_SIDE && file.size < 600 * 1024)) return resolve(original);
        const escala = Math.min(1, MAX_IMAGE_SIDE / maior);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * escala);
        canvas.height = Math.round(img.height * escala);
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(original);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const preservaTransparencia = file.type === 'image/png' || file.type === 'image/gif';
        try {
          resolve(
            preservaTransparencia
              ? canvas.toDataURL('image/png')
              : canvas.toDataURL('image/jpeg', 0.85),
          );
        } catch {
          resolve(original);
        }
      };
      img.onerror = () => resolve(original);
      img.src = original;
    };
    reader.readAsDataURL(file);
  });

const Prontuario: React.FC<ProntuarioProps> = ({ 
  patient, 
  onUpdatePatient,
  patients,
  recordingsList,
  setRecordingsList,
  onSaveAIAnalysis,
  onDeleteTranscription,
  initialRecordDate,
  onClearInitialRecordDate,
  onRecordsChange
}) => {
  // States
  const [records, setRecords] = useState<ClinicalRecord[]>(() => patient.records || []);
  const [selectedRecord, setSelectedRecord] = useState<ClinicalRecord | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<string | null>(null);
  
  // Audio & IA Modals
  const [activeModal, setActiveModal] = useState<'recordings' | 'ai-analysis' | 'transcriptions' | null>(null);
  const [preSelectedRecordingId, setPreSelectedRecordingId] = useState<string | null>(null);
  
  // Editor States
  const [recordDate, setRecordDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [editorHtml, setEditorHtml] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [showAiModal, setShowAiModal] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isAnalyzingAudio, setIsAnalyzingAudio] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [showTableDialog, setShowTableDialog] = useState(false);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  // Devolutiva do "Supervisionar": vive no card escuro abaixo do botão
  // "Salvar Prontuário", nunca dentro do editor.
  const [supervisionResult, setSupervisionResult] = useState<string | null>(null);
  // Seções estruturadas da última supervisão (Matriz de Responsabilidade);
  // registros antigos só têm o texto, então o card cai no formato de texto.
  const [supervisionSections, setSupervisionSections] = useState<SupervisionApiResponse | null>(null);
  const [selectedApproachForAnalysis, setSelectedApproachForAnalysis] = useState<Approach | null>(null);
  const [recordingToAnalyze, setRecordingToAnalyze] = useState<any | null>(null);
  const [showSupervisionModal, setShowSupervisionModal] = useState(false);
  const [recordingToSupervise, setRecordingToSupervise] = useState<any | null>(null);
  const [isSupervising, setIsSupervising] = useState(false);
  const [supervisionError, setSupervisionError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [activeFont, setActiveFont] = useState('Arial');
  const [activeSize, setActiveSize] = useState('14px');
  const [isSavedSuccessfully, setIsSavedSuccessfully] = useState(false);
  const [isSavingRecord, setIsSavingRecord] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sentenceIndexRef = useRef(0);
  const lastSpeechTimeRef = useRef(0);

  // Sync state with patient updates
  useEffect(() => {
    setRecords(patient.records || []);
  }, [patient]);

  // Handle redirection to a specific scheduled date
  useEffect(() => {
    if (initialRecordDate) {
      const existing = (patient.records || []).find(r => r.date === initialRecordDate);
      if (existing) {
        handleSelectRecord(existing);
      } else {
        handleResetEditor();
        setRecordDate(initialRecordDate);
      }
      if (onClearInitialRecordDate) {
        onClearInitialRecordDate();
      }
    }
  }, [initialRecordDate, patient, onClearInitialRecordDate]);

  // Recording Timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
      setRecordingSeconds(0);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (typingTimerRef.current) clearInterval(typingTimerRef.current);
    };
  }, [isRecording]);

  // Rich-text execution wrapper
  const execCmd = (command: string, value: string = '') => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setEditorHtml(editorRef.current.innerHTML);
      editorRef.current.focus();
    }
  };

  const handleFontChange = (font: string) => {
    setActiveFont(font);
    execCmd('fontName', font);
  };

  const handleSizeChange = (size: string) => {
    setActiveSize(size);
    // Maps standard HTML font sizes (1-7)
    const map: Record<string, string> = {
      '12px': '2',
      '14px': '3',
      '16px': '4',
      '18px': '5',
      '20px': '6',
      '24px': '7'
    };
    execCmd('fontSize', map[size] || '3');
  };

  /** Abre o diálogo para escolher linhas e colunas. */
  const handleInsertTable = () => setShowTableDialog(true);

  const confirmInsertTable = () => {
    execCmd('insertHTML', buildTableHtml(tableRows, tableCols));
    setShowTableDialog(false);
  };

  const insertImageFromDataUrl = (dataUrl: string) => {
    const imgHTML = `
        <div style="text-align: center; margin: 15px 0;">
          <img src="${dataUrl}" style="max-width: 100%; max-height: 350px; border-radius: 16px; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);" alt="Imagem de Prontuário" />
        </div>
        <p><br></p>
      `;
    execCmd('insertHTML', imgHTML);
  };

  /** Lê arquivos de imagem e insere no prontuário (upload ou arrastar-e-soltar). */
  const insertImageFiles = async (files: FileList | File[] | null) => {
    const list = Array.from(files ?? []).filter((file) => file.type.startsWith('image/'));
    if (!list.length) return;

    for (const file of list) {
      try {
        const dataUrl = await readImageAsDataUrl(file);
        insertImageFromDataUrl(dataUrl);
      } catch (err) {
        console.error(err);
        alert(`Não foi possível inserir a imagem "${file.name}".`);
      }
    }
  };

  /** Botão da barra de ferramentas: abre o seletor de arquivos da máquina. */
  const handleInsertImage = () => {
    imageInputRef.current?.click();
  };

  const handleImageInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    await insertImageFiles(e.target.files);
    e.target.value = '';
  };

  const handleEditorDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (Array.from(e.dataTransfer?.types ?? []).includes('Files')) {
      e.preventDefault();
      setIsDraggingImage(true);
    }
  };

  const handleEditorDragLeave = () => setIsDraggingImage(false);

  const handleEditorDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from<File>(e.dataTransfer?.files ?? ([] as unknown as FileList)).filter((f) =>
      f.type.startsWith('image/'),
    );
    if (!files.length) return;
    e.preventDefault();
    setIsDraggingImage(false);
    await insertImageFiles(files);
  };

  /** Colar imagem direto do clipboard (print de tela, por exemplo). */
  const handleEditorPaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const files = Array.from<File>(e.clipboardData?.files ?? ([] as unknown as FileList)).filter((f) =>
      f.type.startsWith('image/'),
    );
    if (!files.length) return;
    e.preventDefault();
    await insertImageFiles(files);
  };

  // Audio Recording with Speech-to-Text
  const handleToggleRecording = () => {
    if (isRecording) {
      // STOP RECORDING
      setIsRecording(false);
      
      // Stop Web Speech API if active
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          console.error("Erro ao parar reconhecimento de voz:", e);
        }
        recognitionRef.current = null;
      }
      
      // Clear timers
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    } else {
      // START RECORDING
      setIsRecording(true);
      lastSpeechTimeRef.current = Date.now();
      
      // Start Web Speech API if supported
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        try {
          const rec = new SpeechRecognition();
          rec.continuous = true;
          rec.interimResults = false;
          rec.lang = 'pt-BR';
          
          rec.onresult = (event: any) => {
            const resultIndex = event.resultIndex;
            const transcript = event.results[resultIndex][0].transcript;
            
            // Mark that actual speech was received to pause fallback typing
            lastSpeechTimeRef.current = Date.now();
            
            if (editorRef.current) {
              editorRef.current.focus();
              document.execCommand('insertText', false, ' ' + transcript);
              setEditorHtml(editorRef.current.innerHTML);
            }
          };
          
          rec.onend = () => {
            // Safe cleanup
          };
          
          rec.onerror = (e: any) => {
            console.warn("Erro ou bloqueio no reconhecimento de voz (usando fallback de digitação):", e);
          };
          
          rec.start();
          recognitionRef.current = rec;
        } catch (e) {
          console.error("Erro ao inicializar reconhecimento de voz:", e);
        }
      }

      // Sem texto simulado: se o navegador não suportar ditado, o campo
      // simplesmente não é preenchido — antes o editor recebia frases clínicas
      // prontas a cada 4 segundos, que pareciam transcrição real.
    }
  };

  const handleTriggerTranscription = async (recordingId: string) => {
    const rec = recordingsList.find(r => r.id === recordingId);
    if (!rec) return;

    setActiveModal(null); // Close the recordings modal
    setIsTranscribing(true);

    try {
      await registerAiConsent(patient.id);
      // Transcrição real da API. Sem fallback simulado: se falhar, o usuário
      // precisa saber, em vez de receber um texto que não é da sessão dele.
      const transcriptionText = rec.transcription || (await transcribeRecording(rec));

      const updatedList = recordingsList.map(r => {
        if (r.id === recordingId) {
          return { ...r, transcription: transcriptionText };
        }
        return r;
      });
      setRecordingsList(updatedList);

      if (editorRef.current) {
        editorRef.current.focus();
        const currentText = editorRef.current.innerText.trim();
        const spacer = currentText ? "<br/><br/>" : "";
        const formattedText = `${spacer}<strong>Transcrição Literal da Sessão:</strong><br/>${transcriptionText.replace(/\n/g, '<br/>')}`;
        
        editorRef.current.innerHTML = editorRef.current.innerHTML + formattedText;
        setEditorHtml(editorRef.current.innerHTML);
      }
    } catch (err: any) {
      console.error(err);
      alert("Erro ao realizar a transcrição: " + (err.message || err));
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleTriggerAIAnalysis = (recordingId: string) => {
    const rec = recordingsList.find(r => r.id === recordingId);
    if (!rec) return;

    setRecordingToAnalyze(rec);
    setActiveModal(null); // Close the recordings list modal
    setShowAiModal(true); // Open the approach selection modal
  };

  /**
   * "Supervisionar": diferente da Análise IA, gera uma supervisão completa do
   * caso a partir da sessão (gravação ou texto do prontuário), na linha teórica
   * escolhida pelo usuário.
   */
  const handleTriggerSupervision = (recordingId: string) => {
    const rec = recordingsList.find(r => r.id === recordingId);
    if (!rec) return;
    setRecordingToSupervise(rec);
    setSupervisionError(null);
    setActiveModal(null);
    setShowSupervisionModal(true);
  };

  const handleOpenSupervisionModal = () => {
    const plainText = editorRef.current?.innerText.trim() || '';
    if (!plainText && !recordingToSupervise) {
      setSupervisionError('Grave ou escreva o conteúdo da sessão antes de pedir a supervisão.');
      return;
    }
    setSupervisionError(null);
    setShowSupervisionModal(true);
  };

  const handleRunSupervision = async (approach: Approach) => {
    const recToSupervise = recordingToSupervise;
    setRecordingToSupervise(null);
    setShowSupervisionModal(false);
    setSupervisionError(null);
    setIsSupervising(true);

    try {
      let sessionContent = '';

      if (recToSupervise) {
        // Usa a transcrição existente; se não houver, transcreve o áudio antes.
        sessionContent = recToSupervise.transcription || '';
        if (!sessionContent) {
          await registerAiConsent(patient.id);
          sessionContent = await transcribeRecording(recToSupervise);
          setRecordingsList(prev =>
            prev.map(r => (r.id === recToSupervise.id ? { ...r, transcription: sessionContent } : r)),
          );
        }
        if (!sessionContent) {
          throw new Error('Não foi possível obter a transcrição da sessão para supervisionar.');
        }
      } else {
        sessionContent = editorRef.current?.innerText.trim() || '';
      }

      await registerAiConsent(patient.id);
      // A supervisão roda no backend (chave protegida, consentimento e
      // auditoria) e vai para o card abaixo do editor — o texto do
      // profissional fica intacto.
      const result = await superviseSession(patient.id, sessionContent, approach);
      setSelectedApproachForAnalysis(approach);
      setSupervisionSections(result);
      setSupervisionResult(result.supervision);
      setSupervisionError(null);
    } catch (e: any) {
      console.error(e);
      setSupervisionResult(null);
      setSupervisionError(`Não foi possível gerar a supervisão: ${describeError(e)}`);
    } finally {
      setIsSupervising(false);
    }
  };

  const handleOpenAiAnalysisModal = () => {
    const plainText = editorRef.current?.innerText.trim() || '';
    if (!plainText) {
      alert("Por favor, digite algum texto antes de submeter à análise de IA.");
      return;
    }
    setShowAiModal(true);
  };

  /**
   * Baixa o áudio da gravação e transcreve pela API (a chave da IA fica no
   * backend). Sem texto simulado: falha aqui é falha visível.
   */
  const transcribeRecording = async (rec: any): Promise<string> => {
    const url = rec.url || (await getRecordingPlaybackUrl(rec.id)).url;
    const response = await fetch(url);
    if (!response.ok) throw new Error('Não foi possível baixar o áudio da sessão.');
    const blob = await response.blob();
    const file = new File([blob], rec.name || 'sessao.webm', {
      type: blob.type || 'audio/webm',
    });
    const result = await transcribeAudio(patient.id, file, rec.id);
    const texto = (result?.text || '').trim();
    if (!texto) throw new Error('A transcrição voltou vazia. Verifique a qualidade do áudio.');
    return texto;
  };

  /**
   * Análise IA (gravação ou texto do prontuário).
   *
   * A devolutiva é inserida no corpo do editor — transcrição literal, síntese
   * e fenômenos clínicos e análise na abordagem — para ser editada e salva com
   * o "Salvar Prontuário". O card escuro abaixo é exclusivo da Supervisão.
   */
  const handleRunAiAnalysis = async (approach: Approach) => {
    setSelectedApproachForAnalysis(approach);
    const recToAnalyze = recordingToAnalyze;
    setRecordingToAnalyze(null);
    setShowAiModal(false);

    if (recToAnalyze) setIsAnalyzingAudio(true);
    else setIsAnalyzing(true);

    try {
      await registerAiConsent(patient.id);

      // 1) Conteúdo da sessão: transcrição da gravação ou o texto do prontuário.
      let sessionText = '';
      if (recToAnalyze) {
        sessionText = recToAnalyze.transcription || '';
        if (!sessionText) {
          sessionText = await transcribeRecording(recToAnalyze);
          setRecordingsList((prev) =>
            prev.map((r) => (r.id === recToAnalyze.id ? { ...r, transcription: sessionText } : r)),
          );
        }
      } else {
        sessionText = editorRef.current?.innerText.trim() || '';
      }

      if (!sessionText.trim()) {
        throw new Error('Não há conteúdo para analisar. Grave a sessão ou escreva no prontuário.');
      }

      // 2) Análise real na API (a chave da IA fica só no backend), na
      //    abordagem escolhida — seções 3 a 5 são vinculadas a ela.
      const analysis = await analyzeSessionText(patient.id, sessionText, approach);

      // Matriz de Responsabilidade — botão ANÁLISE IA:
      // 1 Transcrição literal (material do profissional, integral)
      // 2 Panorama da sessão (factual)
      // 3 Leitura teórica · 4 Manejo técnico · 5 Pontos de atenção e risco (na abordagem)
      const ov = analysis.sessionOverview;
      const panorama = [
        `Queixa: ${ov.complaint}`,
        `Enquadre/setting: ${ov.setting}`,
        `Clima relacional: ${ov.relationalClimate}`,
        ov.contextGaps.length
          ? `Lacunas contextuais:\n${ov.contextGaps.map((g) => `• ${g}`).join('\n')}`
          : 'Lacunas contextuais: nenhuma identificada.',
      ].join('\n');
      const manejo = analysis.technicalManagement.map((t, i) => `${i + 1}. ${t}`).join('\n');
      const riscos = analysis.attentionAndRisk.map((t) => `⚠ ${t}`).join('\n');
      const abordagem = escapeHtml(analysis.approach || approach);

      const fonteTranscricao = recToAnalyze
        ? '1 - TRANSCRIÇÃO LITERAL DA SESSÃO:'
        : '1 - TRANSCRIÇÃO LITERAL DA SESSÃO (texto do prontuário):';
      const htmlResult =
        `<p><strong>Análise IA (${abordagem}):</strong></p>` +
        `<p>${fonteTranscricao}<br/>${textToHtml(sessionText)}</p>` +
        `<p><br></p>` +
        `<p>2 - PANORAMA DA SESSÃO:<br/>${textToHtml(panorama)}</p>` +
        `<p><br></p>` +
        `<p>3 - LEITURA TEÓRICA (${abordagem}):<br/>${textToHtml(analysis.theoreticalReading)}</p>` +
        `<p><br></p>` +
        `<p>4 - MANEJO TÉCNICO (${abordagem}):<br/>${textToHtml(manejo || '—')}</p>` +
        `<p><br></p>` +
        `<p>5 - PONTOS DE ATENÇÃO E RISCO (${abordagem}):<br/>${textToHtml(riscos || '—')}</p>` +
        `<p><em>${escapeHtml(analysis.disclaimer)}</em></p>` +
        `<p><br></p>`;

      if (editorRef.current) {
        const currentText = editorRef.current.innerText.trim();
        const spacer = currentText ? '<p><br></p>' : '';
        editorRef.current.innerHTML = editorRef.current.innerHTML + spacer + htmlResult;
        setEditorHtml(editorRef.current.innerHTML);
        editorRef.current.focus();
      }
    } catch (e: any) {
      console.error(e);
      alert(`Não foi possível gerar a análise: ${describeError(e)}`);
    } finally {
      setIsAnalyzing(false);
      setIsAnalyzingAudio(false);
    }
  };

  /**
   * Salva na API. Antes o registro ficava só no estado local e, como o App
   * recarregava o paciente logo em seguida, o histórico voltava vazio.
   */
  const handleSaveRecord = async () => {
    const htmlContent = editorRef.current?.innerHTML || '';
    const plainText = editorRef.current?.innerText.trim() || '';
    const hasImage = /<img\s/i.test(htmlContent);

    if (!plainText && !hasImage) {
      setSaveError("O conteúdo do prontuário está vazio.");
      return;
    }

    setSaveError(null);
    setIsSavingRecord(true);
    const payload = {
      date: recordDate,
      content: htmlContent,
      approach: selectedApproachForAnalysis || selectedRecord?.approach || undefined,
      aiAnalysis: supervisionResult || selectedRecord?.aiAnalysis || undefined,
    };

    try {
      const saved = selectedRecord
        ? await updateClinicalRecord(patient.id, selectedRecord.id, payload)
        : await createClinicalRecord(patient.id, payload);

      const updatedRecords = selectedRecord
        ? records.map(r => r.id === selectedRecord.id ? saved : r)
        : [...records, saved];

      setRecords(updatedRecords);
      onRecordsChange?.(patient.id, updatedRecords);
      setSelectedRecord(saved);
      setIsSavedSuccessfully(true);
      setTimeout(() => setIsSavedSuccessfully(false), 3000);
    } catch (err) {
      console.error(err);
      setSaveError(`Não foi possível salvar o prontuário: ${describeError(err)}`);
    } finally {
      setIsSavingRecord(false);
    }
  };

  const handleDeleteRecord = (id: string) => {
    setRecordToDelete(id);
  };

  const executeDeleteRecord = async (id: string) => {
    try {
      await deleteClinicalRecord(patient.id, id);
    } catch (err) {
      console.error(err);
      alert(`Não foi possível excluir o registro: ${describeError(err)}`);
      return;
    }
    const updatedRecords = records.filter(r => r.id !== id);
    setRecords(updatedRecords);
    onRecordsChange?.(patient.id, updatedRecords);
    if (selectedRecord?.id === id) {
      handleResetEditor();
    }
  };

  const handleResetEditor = () => {
    setSelectedRecord(null);
    setRecordDate(new Date().toISOString().split('T')[0]);
    setEditorHtml('');
    setSupervisionResult(null);
    setSelectedApproachForAnalysis(null);
    setSaveError(null);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  const handleSelectRecord = (record: ClinicalRecord) => {
    setSelectedRecord(record);
    setRecordDate(record.date);
    setEditorHtml(record.content);
    setSupervisionResult(record.aiAnalysis || null);
    setSelectedApproachForAnalysis(record.approach || null);
    setSaveError(null);
    if (editorRef.current) {
      editorRef.current.innerHTML = record.content;
    }
  };

  // Format date helper
  const formatDate = (dateStr: string) => {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    const parts = dateStr.split('-');
    return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr;
  };

  const fontOptions = [
    'Arial', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana', 
    'Trebuchet MS', 'Comic Sans MS', 'Impact', 'Garamond', 'Bookman'
  ];

  const sizeOptions = ['12px', '14px', '16px', '18px', '20px', '24px'];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 sm:gap-10 animate-in fade-in duration-500 relative">
      
      {/* List of Previous Records on Left (3 cols -> 30%) */}
      <div className="lg:col-span-3 space-y-6 max-h-[85vh] overflow-y-auto pr-3 custom-scrollbar">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
            <FileText size={20} /> Histórico de Prontuários
          </h3>
          <button 
            onClick={handleResetEditor}
            className="flex items-center gap-1 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-indigo-100 transition-all shadow-sm"
          >
            <Plus size={14} /> Novo Registro
          </button>
        </div>

        <div className="space-y-4">
          {records.slice().reverse().map(record => (
            <div 
              key={record.id}
              onClick={() => handleSelectRecord(record)}
              className={`w-full p-6 rounded-[2rem] border-2 text-left transition-all cursor-pointer relative group ${selectedRecord?.id === record.id ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-100 scale-[1.01]' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 hover:border-indigo-300'}`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  <Calendar size={14} className={selectedRecord?.id === record.id ? "text-indigo-200" : "text-slate-400"} />
                  <span className="font-black text-sm">{formatDate(record.date)}</span>
                </div>
                <button 
                  onClick={(e) => { e.stopPropagation(); handleDeleteRecord(record.id); }}
                  className={`p-2 rounded-xl transition-all opacity-80 hover:opacity-100 hover:scale-110 active:scale-95 ${selectedRecord?.id === record.id ? 'text-white/85 hover:text-white hover:bg-white/10' : 'text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20'}`}
                  title="Excluir prontuário"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              {record.approach && (
                <div className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-md inline-block mb-3 ${selectedRecord?.id === record.id ? 'bg-white/15 text-indigo-100' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                  {record.approach.split('(')[0]}
                </div>
              )}

              <p 
                className="text-xs line-clamp-3 opacity-90 leading-relaxed font-medium"
                dangerouslySetInnerHTML={{ __html: record.content.replace(/<[^>]*>/g, ' ').substring(0, 150) + '...' }}
              />
            </div>
          ))}

          {records.length === 0 && (
            <div className="p-8 sm:p-16 text-center bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-800">
              <FileText size={48} className="mx-auto mb-4 opacity-20 text-slate-400" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Nenhum prontuário registrado.</p>
              <p className="text-[10px] text-slate-400 mt-2 font-medium">Use a caixa ao lado para iniciar seu primeiro resumo clínico de sessão.</p>
            </div>
          )}
        </div>
      </div>

      {/* Editor Panel on Right (7 cols -> 70%) */}
      <div className="lg:col-span-7 space-y-6">
        <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2rem] sm:rounded-[3.5rem] border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
          
          {/* Header Editor with Date and Title */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b dark:border-slate-800 pb-5">
            <div>
              <h3 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
                {selectedRecord ? 'Editando Prontuário' : 'Novo Registro de Sessão'}
              </h3>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                Paciente: {patient.name}
              </p>
            </div>

            {/* Date Input */}
            <div className="flex items-center gap-3">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Data Clínica:</label>
              <div className="relative">
                <input 
                  type="date"
                  value={recordDate}
                  onChange={e => setRecordDate(e.target.value)}
                  className="bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-xs font-bold text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          {/* Rich Text Editor Toolbar */}
          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-3xl border border-slate-100 dark:border-slate-800 flex flex-wrap gap-4 items-center">
            
            {/* Formatting Group */}
            <div className="flex flex-col items-center">
              <div className="flex gap-1 bg-white dark:bg-slate-900 p-1.5 rounded-xl border dark:border-slate-800 shadow-sm">
                <button onClick={() => execCmd('bold')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Negrito">
                  <span className="font-black text-xs w-3.5 h-3.5 flex items-center justify-center leading-none">N</span>
                </button>
                <button onClick={() => execCmd('italic')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Itálico">
                  <Italic size={15} />
                </button>
                <button onClick={() => execCmd('underline')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Sublinhado">
                  <span className="underline font-black text-xs w-3.5 h-3.5 flex items-center justify-center leading-none">S</span>
                </button>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Formato</span>
            </div>

            {/* Align Group */}
            <div className="flex flex-col items-center">
              <div className="flex gap-1 bg-white dark:bg-slate-900 p-1.5 rounded-xl border dark:border-slate-800 shadow-sm">
                <button onClick={() => execCmd('justifyLeft')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Alinhar à Esquerda">
                  <AlignLeft size={15} />
                </button>
                <button onClick={() => execCmd('justifyCenter')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Centralizar">
                  <AlignCenter size={15} />
                </button>
                <button onClick={() => execCmd('justifyRight')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Alinhar à Direita">
                  <AlignRight size={15} />
                </button>
                <button onClick={() => execCmd('justifyFull')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" title="Justificado">
                  <AlignJustify size={15} />
                </button>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Alinhamento</span>
            </div>

            {/* Fonts Name Select */}
            <div className="flex flex-col items-center">
              <select 
                value={activeFont}
                onChange={e => handleFontChange(e.target.value)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 h-[38px] w-[110px]"
              >
                {fontOptions.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Fonte</span>
            </div>

            {/* Font Size Select */}
            <div className="flex flex-col items-center">
              <select 
                value={activeSize}
                onChange={e => handleSizeChange(e.target.value)}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl px-3 py-2 text-[11px] font-bold text-slate-700 dark:text-slate-300 shadow-sm focus:outline-none focus:ring-1 focus:ring-indigo-500 h-[38px]"
              >
                {sizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Tamanho</span>
            </div>

            {/* Elements Insert Group */}
            <div className="flex flex-col items-center">
              <div className="flex gap-1 bg-white dark:bg-slate-900 p-1.5 rounded-xl border dark:border-slate-800 shadow-sm">
                <button onClick={handleInsertTable} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors" title="Inserir Tabela">
                  <Table size={15} />
                </button>
                <button onClick={handleInsertImage} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors" title="Inserir Imagem">
                  <ImageIcon size={15} />
                </button>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Inserir</span>
            </div>

            {/* History Actions */}
            <div className="flex flex-col items-center">
              <div className="flex gap-1 bg-white dark:bg-slate-900 p-1.5 rounded-xl border dark:border-slate-800 shadow-sm">
                <button onClick={() => execCmd('undo')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors" title="Desfazer">
                  <Undo size={15} />
                </button>
                <button onClick={() => execCmd('redo')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 rounded-lg transition-colors" title="Refazer">
                  <Redo size={15} />
                </button>
                <button onClick={() => execCmd('removeFormat')} className="p-2 text-slate-600 dark:text-slate-300 hover:text-rose-600 hover:bg-slate-50 rounded-lg transition-colors" title="Limpar Formatação">
                  <Eraser size={15} />
                </button>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Ações</span>
            </div>

            {/* Audio & IA Modules Group */}
            <div className="flex flex-col items-center">
              <div className="flex gap-1.5 bg-white dark:bg-slate-900 p-1.5 rounded-xl border dark:border-slate-800 shadow-sm">
                <button 
                  onClick={() => setActiveModal('recordings')} 
                  className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" 
                  title="Análise com IA"
                >
                  <Mic size={15} />
                </button>
                <button 
                  onClick={() => setActiveModal('recordings')} 
                  className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" 
                  title="Análise Avançada IA"
                >
                  <Sparkles size={15} />
                </button>
                <button 
                  onClick={() => setActiveModal('recordings')} 
                  className="p-2 text-slate-600 dark:text-slate-300 hover:text-indigo-600 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg transition-colors" 
                  title="Transcrições de Áudio"
                >
                  <FileText size={15} />
                </button>
              </div>
              <span className="text-[9px] font-black text-slate-400 uppercase tracking-wider mt-1">Módulos de IA</span>
            </div>

            {/* Dois fluxos distintos, lado a lado:
                - ANÁLISE IA: devolutiva inserida no corpo do editor;
                - SUPERVISIONAR: devolutiva no card escuro abaixo do "Salvar". */}
            <div className="flex items-start gap-3 ml-auto">
              <div className="flex flex-col items-center">
                <button
                  onClick={handleOpenAiAnalysisModal}
                  disabled={isAnalyzing || isAnalyzingAudio}
                  className="p-3.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-60 transition-all shadow-md flex items-center justify-center font-black gap-2 text-[10px] uppercase tracking-wider"
                  title="Gerar análise IA do prontuário na linha teórica escolhida (resultado no editor)"
                >
                  {isAnalyzing ? <Loader2 size={16} className="animate-spin" /> : <BrainCircuit size={16} />} ANÁLISE IA
                </button>
                <span className="text-[9px] font-black text-emerald-600 normal-case tracking-wider mt-1">com linha teórica específica</span>
              </div>
              <div className="flex flex-col items-center">
                <button
                  onClick={handleOpenSupervisionModal}
                  disabled={isSupervising}
                  className="p-3.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-60 transition-all shadow-md flex items-center justify-center font-black gap-2 text-[10px] uppercase tracking-wider"
                  title="Gerar supervisão clínica completa da sessão (resultado no card abaixo)"
                >
                  {isSupervising ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} SUPERVISIONAR
                </button>
                <span className="text-[9px] font-black text-indigo-500 normal-case tracking-wider mt-1">supervisão completa da sessão</span>
              </div>
            </div>

          </div>

          {/* Editor Area */}
          <div className="relative">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageInputChange}
              className="hidden"
            />
            <div
              ref={editorRef}
              contentEditable
              onInput={e => setEditorHtml(e.currentTarget.innerHTML)}
              onDragOver={handleEditorDragOver}
              onDragLeave={handleEditorDragLeave}
              onDrop={handleEditorDrop}
              onPaste={handleEditorPaste}
              className={`w-full min-h-[350px] max-h-[500px] overflow-y-auto bg-slate-50/50 dark:bg-slate-900/60 border rounded-[2rem] p-5 sm:p-8 text-sm text-slate-800 dark:text-slate-100 caret-indigo-600 dark:caret-indigo-300 selection:bg-indigo-200/40 dark:selection:bg-indigo-500/30 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium leading-relaxed ${isDraggingImage ? 'border-indigo-500 border-dashed ring-4 ring-indigo-500/10' : 'border-slate-200 dark:border-slate-800'}`}
              style={{ fontFamily: activeFont, fontSize: activeSize }}
            />
            {isDraggingImage && (
              <div className="absolute inset-0 rounded-[2rem] bg-indigo-50/80 dark:bg-indigo-950/60 backdrop-blur-sm flex items-center justify-center pointer-events-none z-40">
                <p className="text-xs font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-300 flex items-center gap-2">
                  <ImageIcon size={16} /> Solte a imagem para inserir no prontuário
                </p>
              </div>
            )}
            {!editorHtml && (
              <div className="absolute top-8 left-8 text-slate-400 pointer-events-none text-sm italic font-medium">
                Descreva o resumo do que foi ouvido e observado durante a sessão terapêutica...
              </div>
            )}
            {(isTranscribing || isAnalyzingAudio || isAnalyzing) && (
              <div className="absolute inset-0 bg-white/85 dark:bg-slate-900/85 backdrop-blur-sm rounded-[2rem] flex flex-col items-center justify-center space-y-4 z-50 animate-in fade-in duration-300">
                <Loader2 className="animate-spin text-indigo-600 dark:text-indigo-400" size={32} />
                <p className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-300">
                  {isTranscribing
                    ? "Transcrevendo áudio com IA..."
                    : isSupervising
                      ? "Gerando supervisão clínica..."
                      : "Gerando Análise IA..."}
                </p>
                <p className="text-[10px] text-slate-400 font-medium">
                  {isTranscribing
                    ? "Aguarde enquanto convertemos a sessão em texto clínico."
                    : isSupervising
                      ? "Aguarde enquanto o backend processa a supervisão pela IA."
                      : "Aguarde enquanto processamos os dados sob a abordagem escolhida."}
                </p>
              </div>
            )}
          </div>

          {/* Show clinical approach analysis tag if defined */}
          {selectedApproachForAnalysis && (
            <div className="flex items-center gap-2 text-[10px] font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-xl border border-indigo-100 dark:border-indigo-900/40 uppercase tracking-widest">
              <Sparkles size={14} /> Abordagem de Análise IA: {selectedApproachForAnalysis}
            </div>
          )}

          {supervisionError && (
            <div className="flex items-start gap-2 text-[11px] font-bold text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/30 px-4 py-3 rounded-2xl border border-rose-100 dark:border-rose-900/50">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{supervisionError}</span>
            </div>
          )}

          {/* Action Footer */}
          <div className="flex justify-between items-center pt-4 border-t dark:border-slate-800">
            {saveError && (
              <p className="text-xs font-bold text-rose-500 flex items-center gap-1.5">
                <AlertCircle size={16} /> {saveError}
              </p>
            )}
            {isSavedSuccessfully && (
              <p className="text-xs font-black text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 uppercase tracking-wider">
                <CheckCircle2 size={16} /> Prontuário Salvo com Sucesso!
              </p>
            )}
            {!saveError && !isSavedSuccessfully && <div />}

            <div className="flex gap-4">
              {selectedRecord && (
                <button 
                  onClick={handleResetEditor}
                  className="px-6 py-3 border border-slate-200 dark:border-slate-700 hover:border-indigo-500 text-slate-600 dark:text-slate-300 rounded-2xl text-[10px] font-black uppercase tracking-wider transition-all"
                >
                  Criar Novo
                </button>
              )}
              <button 
                onClick={handleSaveRecord}
                disabled={isSavingRecord}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white px-5 sm:px-8 py-3 rounded-2xl text-[10px] font-black flex items-center gap-2 uppercase tracking-widest transition-all shadow-md"
              >
                {isSavingRecord ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Salvar Prontuário
              </button>
            </div>
          </div>

        </div>

        {/* Card escuro/azul exclusivo da Supervisão Clínica IA — sempre abaixo
            do botão "Salvar Prontuário" e desvinculado do editor. */}
        {(isSupervising || supervisionResult) && (
          <div className="bg-indigo-950 dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] text-white p-5 sm:p-10 border border-indigo-900/50 dark:border-slate-800 shadow-2xl relative overflow-hidden transition-all duration-500">
            <div className="flex items-center justify-between mb-6 border-b border-indigo-900/50 pb-5">
              <h4 className="text-lg font-black uppercase tracking-[0.2em] flex items-center gap-3">
                <BrainCircuit size={28} className="text-indigo-300 animate-pulse" /> 
                Análise de Supervisão Clínica IA
              </h4>
              {selectedApproachForAnalysis && (
                <span className="text-[9px] font-black bg-indigo-500/20 text-indigo-200 px-3 py-1.5 rounded-full border border-indigo-400/20 uppercase tracking-widest">
                  {selectedApproachForAnalysis.split('(')[0]}
                </span>
              )}
            </div>

            {isSupervising ? (
              <div className="py-12 flex flex-col items-center justify-center text-center space-y-4">
                <Loader2 className="animate-spin text-indigo-400" size={48} />
                <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-200">
                  O Supervisor Clínico IA está analisando o prontuário...
                </p>
                <p className="text-[10px] text-indigo-300 font-medium italic">
                  Isso pode levar alguns segundos enquanto mapeamos os fenômenos clínicos.
                </p>
              </div>
            ) : (
              <div className="text-sm leading-relaxed text-indigo-100 font-medium max-h-[560px] overflow-y-auto custom-scrollbar pr-2">
                {supervisionSections && supervisionSections.supervision === supervisionResult ? (
                  /* Matriz de Responsabilidade — botão SUPERVISIONAR:
                     1 Síntese (sintética) · 2 Perguntas · 3 Análise de Supervisão · 4 Plano (na abordagem) */
                  <div className="space-y-7">
                    <section>
                      <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-300 mb-2">1. Síntese e Fenômenos Clínicos</h5>
                      <p className="whitespace-pre-wrap">{supervisionSections.clinicalSynthesis}</p>
                    </section>
                    <section>
                      <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-300 mb-2">2. Perguntas de Supervisão</h5>
                      <ul className="space-y-2 list-disc pl-5">
                        {supervisionSections.supervisionQuestions.map((q, i) => <li key={i}>{q}</li>)}
                      </ul>
                    </section>
                    <section>
                      <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-300 mb-2">3. Análise de Supervisão Clínica IA</h5>
                      <p className="whitespace-pre-wrap">{supervisionSections.clinicalSupervision}</p>
                    </section>
                    <section>
                      <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-indigo-300 mb-2">4. Plano para a Próxima Sessão</h5>
                      <ol className="space-y-2 list-decimal pl-5">
                        {supervisionSections.nextSessionPlan.map((p, i) => <li key={i}>{p}</li>)}
                      </ol>
                    </section>
                    <p className="text-[10px] italic text-indigo-300/80 border-t border-indigo-900/50 pt-4">{supervisionSections.disclaimer}</p>
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap">{supervisionResult}</div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* IA Therapeutic Lines Modal Selection */}
      {showAiModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3.5rem] w-full max-w-4xl p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">
            
            <div className="flex justify-between items-start border-b dark:border-slate-800 pb-6 mb-8">
              <div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                  <Sparkles className="text-indigo-500" size={24} /> Escolha a Abordagem da Análise IA
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  {recordingToAnalyze
                    ? `A sessão "${recordingToAnalyze.name}" será transcrita e analisada sob a perspectiva escolhida; o resultado entra no editor do prontuário.`
                    : 'O texto do prontuário será analisado sob a perspectiva teórica escolhida; o resultado entra no editor, pronto para edição e salvamento.'}
                </p>
              </div>
              <button 
                onClick={() => { setShowAiModal(false); setRecordingToAnalyze(null); }}
                className="text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-widest p-2 bg-slate-50 dark:bg-slate-800 rounded-xl"
              >
                Fechar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(Approach).map(approachVal => (
                <button
                  key={approachVal}
                  onClick={() => handleRunAiAnalysis(approachVal)}
                  className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 border border-slate-100 dark:border-slate-800 hover:border-indigo-300 rounded-2xl text-left transition-all group"
                >
                  <div className="overflow-hidden">
                    <p className="font-bold text-xs text-slate-800 dark:text-slate-200 truncate group-hover:text-indigo-600 transition-colors">
                      {approachVal}
                    </p>
                  </div>
                  <Play size={12} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                </button>
              ))}
            </div>

          </div>
        </div>
      )}

      {/* Escolha da linha teórica para a Supervisão Clínica completa */}
      {/* Inserir tabela: o usuário escolhe linhas e colunas, como no Word. */}
      {showTableDialog && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md z-[210] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] w-full max-w-sm p-5 sm:p-8 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-black dark:text-white tracking-tight flex items-center gap-2">
                <Table size={18} className="text-indigo-500" /> Inserir Tabela
              </h3>
              <button onClick={() => setShowTableDialog(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label htmlFor="tabela-linhas" className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Linhas
                </label>
                <input
                  id="tabela-linhas"
                  type="number"
                  min={1}
                  max={TABLE_MAX_ROWS}
                  value={tableRows}
                  onChange={(e) => setTableRows(clampRows(Number(e.target.value)))}
                  className="w-full p-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm dark:text-slate-100 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="tabela-colunas" className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">
                  Colunas
                </label>
                <input
                  id="tabela-colunas"
                  type="number"
                  min={1}
                  max={TABLE_MAX_COLS}
                  value={tableCols}
                  onChange={(e) => setTableCols(clampCols(Number(e.target.value)))}
                  className="w-full p-3.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm dark:text-slate-100 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none focus:border-indigo-400"
                />
              </div>
            </div>

            <p className="text-[10px] font-bold text-slate-400 mt-4">
              A tabela é inserida vazia, com a primeira linha como cabeçalho.
            </p>

            <div className="flex gap-3 mt-8">
              <button
                onClick={() => setShowTableDialog(false)}
                className="flex-1 p-3.5 border border-slate-200 dark:border-slate-700 rounded-2xl font-black text-slate-500 uppercase text-[10px] tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={confirmInsertTable}
                className="flex-1 p-3.5 bg-indigo-600 text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-indigo-700 transition-all shadow-lg"
              >
                Inserir
              </button>
            </div>
          </div>
        </div>
      )}

      {showSupervisionModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3.5rem] w-full max-w-4xl p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">

            <div className="flex justify-between items-start border-b dark:border-slate-800 pb-6 mb-8">
              <div>
                <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                  <BrainCircuit className="text-indigo-500" size={24} /> Escolha a Linha da Supervisão
                </h3>
                <p className="text-xs text-slate-500 mt-1 font-medium">
                  {recordingToSupervise
                    ? `A sessão "${recordingToSupervise.name}" será supervisionada de forma completa na abordagem escolhida.`
                    : 'O conteúdo do prontuário será supervisionado de forma completa na abordagem escolhida.'}
                </p>
              </div>
              <button
                onClick={() => { setShowSupervisionModal(false); setRecordingToSupervise(null); }}
                className="text-slate-400 hover:text-slate-600 text-xs font-bold uppercase tracking-widest p-2 bg-slate-50 dark:bg-slate-800 rounded-xl"
              >
                Fechar
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(Approach).map(approachVal => (
                <button
                  key={approachVal}
                  onClick={() => handleRunSupervision(approachVal)}
                  className="flex items-center justify-between p-5 bg-slate-50 dark:bg-slate-800/50 hover:bg-indigo-50 dark:hover:bg-indigo-950/20 border border-slate-100 dark:border-slate-800 hover:border-indigo-300 rounded-2xl text-left transition-all group"
                >
                  <div className="overflow-hidden">
                    <p className="font-bold text-xs text-slate-800 dark:text-slate-200 truncate group-hover:text-indigo-600 transition-colors">
                      {approachVal}
                    </p>
                  </div>
                  <Play size={12} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
                </button>
              ))}
            </div>

          </div>
        </div>
      )}

      {/* Audio & IA Modals */}
      {activeModal === 'recordings' && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3.5rem] w-full max-w-6xl p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar relative">
            <button 
              onClick={() => { setActiveModal(null); setPreSelectedRecordingId(null); }}
              className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 p-2 bg-slate-100 dark:bg-slate-800 rounded-2xl transition-all hover:scale-105 active:scale-95"
              title="Fechar"
            >
              <X size={20} />
            </button>
            <div className="mb-8 pr-12">
              <h3 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-3">
                <Sparkles className="text-indigo-500 shrink-0" size={30} /> Análise com IA
              </h3>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                Grave, transcreva e interprete o conteúdo de suas sessões, com suporte de IA.
              </p>
            </div>
            <div className="mt-4">
              <Recordings
                patients={patients}
                recordings={recordingsList}
                onRecordingsChange={setRecordingsList}
                currentPatientId={patient.id}
                /* Dentro do prontuário o paciente já está definido. */
                lockedPatientId={patient.id}
                onTriggerAIAnalysis={handleTriggerAIAnalysis}
                onTriggerTranscription={handleTriggerTranscription}
                onTriggerSupervision={handleTriggerSupervision}
              />
            </div>
          </div>
        </div>
      )}

      {activeModal === 'ai-analysis' && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3.5rem] w-full max-w-5xl p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar relative">
            <button 
              onClick={() => { setActiveModal(null); setPreSelectedRecordingId(null); }}
              className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 p-2 bg-slate-100 dark:bg-slate-800 rounded-2xl transition-all hover:scale-105 active:scale-95"
              title="Fechar"
            >
              <X size={20} />
            </button>
            <div className="mb-8 pr-12">
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <Sparkles className="text-indigo-500" size={24} /> Análise IA Clínica
              </h3>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                Gere e gerencie análises clínicas detalhadas das sessões sob medida.
              </p>
            </div>
            <div className="mt-4">
              <AIAnalysis 
                patients={patients} 
                recordings={recordingsList} 
                onSaveToPatient={onSaveAIAnalysis} 
                initialRecordingId={preSelectedRecordingId}
              />
            </div>
          </div>
        </div>
      )}

      {activeModal === 'transcriptions' && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-md z-[200] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3.5rem] w-full max-w-5xl p-5 sm:p-10 border border-slate-200 dark:border-slate-800 shadow-2xl animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar relative">
            <button 
              onClick={() => { setActiveModal(null); setPreSelectedRecordingId(null); }}
              className="absolute top-8 right-8 text-slate-400 hover:text-slate-600 p-2 bg-slate-100 dark:bg-slate-800 rounded-2xl transition-all hover:scale-105 active:scale-95"
              title="Fechar"
            >
              <X size={20} />
            </button>
            <div className="mb-8 pr-12">
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <FileText className="text-indigo-500" size={24} /> Transcrições de Áudio
              </h3>
              <p className="text-xs text-slate-500 mt-1 font-medium">
                Acesse as transcrições literais de cada áudio processado.
              </p>
            </div>
            <div className="mt-4">
              <Transcriptions 
                recordings={recordingsList} 
                onRecordingsChange={setRecordingsList} 
                onDeleteTranscription={onDeleteTranscription} 
                initialRecordingId={preSelectedRecordingId}
              />
            </div>
          </div>
        </div>
      )}

      {recordToDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999] animate-in fade-in duration-200">
          <div className="bg-white dark:bg-slate-900 p-5 sm:p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-800 shadow-2xl max-w-md w-full mx-4 space-y-6 animate-in zoom-in-95 duration-200">
            <div className="flex items-center gap-4 text-rose-500">
              <div className="w-12 h-12 bg-rose-50 dark:bg-rose-950/30 rounded-2xl flex items-center justify-center shrink-0">
                <AlertCircle size={24} />
              </div>
              <h3 className="text-xl font-black uppercase tracking-tight">Confirmar Exclusão</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
              Quer mesmo apagar? Essa operação não poderá ser desfeita.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button 
                onClick={() => setRecordToDelete(null)}
                className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-800 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800 transition-all cursor-pointer"
              >
                Cancelar
              </button>
              <button 
                onClick={() => {
                  if (recordToDelete) {
                    executeDeleteRecord(recordToDelete);
                    setRecordToDelete(null);
                  }
                }}
                className="px-5 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-widest shadow-lg shadow-rose-100 dark:shadow-none transition-all hover:scale-105 active:scale-95 cursor-pointer"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// As antigas funções de conteúdo simulado (transcrição, síntese e plano)
// foram removidas: elas faziam a IA "responder" sem analisar nada, com textos
// que não tinham relação com a sessão do paciente.
export const audioUrlToBase64 = async (url: string): Promise<{ data: string; mimeType: string }> => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Não foi possível acessar o arquivo de áudio.");
    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === 'string') {
          const base64String = reader.result.split(',')[1];
          resolve({ data: base64String, mimeType: blob.type || 'audio/webm' });
        } else {
          reject(new Error("Erro ao processar o conteúdo do arquivo."));
        }
      };
      reader.onerror = () => reject(new Error("Falha ao ler o arquivo."));
      reader.readAsDataURL(blob);
    });
  } catch (e: any) {
    throw new Error(e.message || "Não foi possível acessar o arquivo de áudio.");
  }
};

export default Prontuario;
